import {
  FetchUserAttributesOutput,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
} from "aws-amplify/auth";
import {
  saveToClientStorage,
  getFromClientStorage,
  deleteFromClientSTorage,
} from "./indexedDb/ClientStorage";
import {
  PROFILE_EXPIRATION,
  SIGNED_OUT_LOCATION,
  PROFILE_REFRESH_CADENCE,
} from "./constants";

/**
 * Custom error thrown when attempting to access user data without being signed in
 */
export class UserNotSignedInError extends Error {
  constructor(message = "User is not signed in. Please sign in first.") {
    super(message);
    this.name = "UserNotSignedInError";
  }
}

/**
 * Signs out the current user using Amplify's built-in sign out,
 * clears credentials, and optionally returns the signed out location for redirect.
 *
 * @returns The signed out location if one was stored, otherwise undefined
 *
 * @example
 * ```typescript
 * const redirectLocation = await signOut();
 * if (redirectLocation) {
 *   window.location.href = redirectLocation;
 * }
 * ```
 */
export async function signOut(): Promise<string | undefined> {
  try {
    // Store the current location before signing out
    await saveToClientStorage(SIGNED_OUT_LOCATION, window.location.href);

    // Clear stored data
    await deleteFromClientSTorage(PROFILE_EXPIRATION);

    // Use Amplify's built-in sign out
    await amplifySignOut();

    return window.location.href;
  } catch (error) {
    console.error("Error during sign out:", error);
    throw new Error(`Failed to sign out: ${error}`);
  }
}

/**
 * Checks if the current user session has expired based on the stored
 * profile expiration time.
 *
 * @returns true if the session has expired, false otherwise
 *
 * @example
 * ```typescript
 * if (await isSessionExpired()) {
 *   await signOut();
 *   // Redirect to login
 * }
 * ```
 */
export async function isSessionExpired(): Promise<boolean> {
  try {
    const expirationStr = await getFromClientStorage(PROFILE_EXPIRATION);

    if (!expirationStr) {
      // No expiration stored means no active session
      return true;
    }

    const expirationTime = parseInt(expirationStr, 10);
    const currentTime = new Date().getTime();

    return currentTime >= expirationTime;
  } catch (error) {
    console.error("Error checking session expiration:", error);
    // If we can't determine expiration, assume expired for safety
    return true;
  }
}

/**
 * Gets the remaining time until session expiration in milliseconds.
 *
 * @returns The remaining time in milliseconds, or 0 if expired/no session
 *
 * @example
 * ```typescript
 * const remainingMs = await getSessionTimeRemaining();
 * const remainingMinutes = Math.floor(remainingMs / 1000 / 60);
 * console.log(`Session expires in ${remainingMinutes} minutes`);
 * ```
 */
export async function getSessionTimeRemaining(): Promise<number> {
  try {
    const expirationStr = await getFromClientStorage(PROFILE_EXPIRATION);

    if (!expirationStr) {
      return 0;
    }

    const expirationTime = parseInt(expirationStr, 10);
    const currentTime = new Date().getTime();
    const remaining = expirationTime - currentTime;

    return remaining > 0 ? remaining : 0;
  } catch (error) {
    console.error("Error getting session time remaining:", error);
    return 0;
  }
}

/**
 * Refreshes the session by updating the expiration time.
 * This should be called when the user performs an action that indicates
 * they're still active.
 *
 * @example
 * ```typescript
 * // Refresh session on user activity
 * document.addEventListener('click', async () => {
 *   await refreshSession();
 * });
 * ```
 */
export async function refreshSession(): Promise<void> {
  try {
    const newExpiration = new Date();
    newExpiration.setHours(newExpiration.getHours() + PROFILE_REFRESH_CADENCE);
    await saveToClientStorage(PROFILE_EXPIRATION, `${newExpiration.getTime()}`);
  } catch (error) {
    console.error("Error refreshing session:", error);
    throw new Error(`Failed to refresh session: ${error}`);
  }
}

/**
 * Checks if a user is currently signed in by verifying that credentials
 * are loaded and the session hasn't expired.
 *
 * @returns true if user is signed in with valid session, false otherwise
 *
 * @example
 * ```typescript
 * if (!(await isSignedIn())) {
 *   // Redirect to login
 * }
 * ```
 */
export async function isSignedIn(): Promise<boolean> {
  try {
    // Check if session has expired
    const expired = await isSessionExpired();
    if (expired) {
      return false;
    }

    // Check if credentials are loaded in the provider
    const expirationStr = await getFromClientStorage(PROFILE_EXPIRATION);
    return !!expirationStr;
  } catch (error) {
    console.error("Error checking sign in status:", error);
    return false;
  }
}

/**
 * Fetches the current authenticated user data using Amplify's getCurrentUser and ID token claims.
 * If the user is not signed in or the session has expired, throws a UserNotSignedInError.
 *
 * @returns The authenticated user with their attributes
 * @throws {UserNotSignedInError} If the user is not signed in or session has expired
 *
 * @example
 * ```typescript
 * try {
 * const userAttributes = await fetchCurrentUser();
 * if (userAttributes) {
 *   console.log("Email:", userAttributes.email);
 *   console.log("Name:", userAttributes.name);
 *   console.log("Sub:", userAttributes.sub);
 * }
 * } catch (error) {
 *   if (error instanceof UserNotSignedInError) {
 *     // Redirect to login page
 *     window.location.href = "/login";
 *   }
 * }
 * ```
 */
export async function fetchCurrentUser(): Promise<FetchUserAttributesOutput> {
  // Check if user is signed in first
  const signedIn = await isSignedIn();
  if (!signedIn) {
    throw new UserNotSignedInError();
  }

  try {
    // Get current user and session to extract user attributes
    const { userId } = await getCurrentUser();
    const session = await fetchAuthSession();

    // Extract user attributes from ID token
    const idToken = session.tokens?.idToken;
    if (!idToken) {
      throw new UserNotSignedInError(
        "Unable to retrieve user data. Please sign in again.",
      );
    }

    // ID token payload contains user attributes
    const userAttributes: FetchUserAttributesOutput = {
      sub: userId,
      email: idToken.payload.email as string,
      email_verified: idToken.payload.email_verified as string,
      name: idToken.payload.name as string,
      given_name: idToken.payload.given_name as string,
      family_name: idToken.payload.family_name as string,
      // Add any other custom attributes from the ID token
      ...(idToken.payload["custom:LDAP_GROUP"] && {
        "custom:LDAP_GROUP": idToken.payload["custom:LDAP_GROUP"] as string,
      }),
    };

    return userAttributes;
  } catch (error) {
    if (error instanceof UserNotSignedInError) {
      throw error;
    }
    throw new UserNotSignedInError(
      "Unable to retrieve user data. Please sign in again.",
    );
  }
}
